import type { ReactNode } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import type { Stage, Opportunity, ContactSummary } from '../../../types';
import { formatCurrency, calculateStageTotal } from '../../../hooks/usePipelineData';
import { DealCard } from './DealCard';
import styles from './StageColumn.module.css';

interface StageColumnProps {
  stage: Stage;
  opportunities: Opportunity[];
  contacts: Map<string, ContactSummary>;
  onViewDeal: (id: string) => void;
  onEditDeal: (id: string) => void;
}

export function StageColumn({
  stage,
  opportunities,
  contacts,
  onViewDeal,
  onEditDeal,
}: StageColumnProps): ReactNode {
  const totalValue = calculateStageTotal(opportunities, stage.id);
  const dealCount = opportunities.filter(o => o.stage_id === stage.id).length;
  const stageOpps = opportunities.filter(o => o.stage_id === stage.id);

  return (
    <div className={styles.column}>
      <div className={styles.header} style={{ '--stage-color': stage.color } as React.CSSProperties}>
        <div className={styles.headerTop}>
          <span className={styles.colorDot} />
          <h2 className={styles.stageName}>{stage.name}</h2>
          <span className={styles.count}>{dealCount}</span>
        </div>
        <div className={styles.total}>
          {formatCurrency(totalValue)}
        </div>
      </div>

      <Droppable droppableId={stage.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`${styles.cardList} ${snapshot.isDraggingOver ? styles.draggingOver : ''}`}
          >
            {stageOpps.map((opp, index) => (
              <Draggable key={opp.id} draggableId={opp.id} index={index}>
                {(dragProvided, dragSnapshot) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                  >
                    <DealCard
                      opportunity={opp}
                      contact={opp.contact_id ? contacts.get(opp.contact_id) : undefined}
                      stageColor={stage.color}
                      onView={onViewDeal}
                      onEdit={onEditDeal}
                      isDragging={dragSnapshot.isDragging}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}

            {stageOpps.length === 0 && !snapshot.isDraggingOver && (
              <div className={styles.emptyState}>
                No deals in this stage
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}
